import { enumeratePlays } from '../game/MoveValidator.js';
import type { Tile, Turn } from '../game/types.js';
import {
  applyTurn,
  nextTileSlotForTurn,
  rectsOverlap,
  type ArmEndpoint,
  type PlacedTile,
} from '../render/geometry.js';
import type { RackTilePlacement } from '../render/HandRenderer.js';
import { rectBounds, type TileRect } from '../render/TileRenderer.js';
import type { Viewport } from '../render/Viewport.js';
import { DRAG_START_THRESHOLD, DROP_TOLERANCE_WORLD } from '../utils/constants.js';
import type { PointerInteraction } from './InputHandler.js';

interface Point {
  readonly x: number;
  readonly y: number;
}

/**
 * Layout the dispatcher hit-tests against. Rack placements are in screen
 * space (don't move with the viewport); arm endpoints + placed tiles + the
 * chain center are in WORLD space — the dispatcher applies
 * `viewport.screenToWorld` before the distance check.
 */
export interface DispatchLayout {
  readonly rackPlacements: readonly RackTilePlacement[];
  readonly armEnds: readonly ArmEndpoint[];
  /** Every tile currently on the table; used for collision-checking drop slots. */
  readonly placedTiles: readonly PlacedTile[];
  readonly chainCenter: { readonly x: number; readonly y: number };
}

export interface PointerDispatcherOptions {
  readonly viewport: Viewport;
  readonly getLocalHand: () => readonly Tile[];
  readonly getCurrentChain: () => import('../game/types.js').Chain;
  readonly getForcedLeadTile: () => Tile | null;
  readonly getLayout: () => DispatchLayout;
  readonly isInputBlocked: () => boolean;
  readonly onCommitPlay: (tile: Tile, armIndex: number, turn: Turn) => void;
  readonly onUserControlledViewport: () => void;
}

/**
 * A single drop slot offered to the player when they're dragging onto an
 * arm. Each legal arm produces up to 3 slots — straight, fold-left,
 * fold-right — filtered to exclude any whose rect would overlap an
 * existing tile.
 */
export interface CandidateSlot {
  readonly armIndex: number;
  readonly turn: Exclude<Turn, 'auto'>;
  readonly rect: TileRect;
}

export interface DispatcherSnapshot {
  /** Tile being dragged (past threshold), or null. */
  readonly draggingTileIndex: number | null;
  readonly draggingTile: Tile | null;
  /** Current ghost center in screen coords (while dragging). */
  readonly ghostScreen: Point | null;
  /** Every non-colliding drop slot offered for the dragged tile. */
  readonly candidateSlots: readonly CandidateSlot[];
  /** Slot the ghost is currently within snap radius of (or null). */
  readonly snapSlot: { readonly armIndex: number; readonly turn: Exclude<Turn, 'auto'> } | null;
  /**
   * True when the chain is empty AND the dragged tile is a legal lead
   * (under Телефон's forced-lead rule, only the forced tile satisfies this).
   * Renderer reads this to decide whether to show the lead drop ring.
   */
  readonly canLead: boolean;
  /** True when the ghost is within snap radius of the chain center for a lead. */
  readonly snapToLead: boolean;
}

const EMPTY_SNAPSHOT: DispatcherSnapshot = {
  draggingTileIndex: null,
  draggingTile: null,
  ghostScreen: null,
  candidateSlots: [],
  snapSlot: null,
  canLead: false,
  snapToLead: false,
};

const TURN_CHOICES: readonly Exclude<Turn, 'auto'>[] = ['straight', 'left', 'right'];

export interface PlacedAxes {
  readonly horizontal: boolean;
  readonly vertical: boolean;
}

/** Which axes the chain's non-lead tiles already occupy. */
export function placedAxes(tiles: readonly PlacedTile[]): PlacedAxes {
  let horizontal = false;
  let vertical = false;
  for (const t of tiles) {
    if (t.direction === null) continue;
    if (t.direction.dx !== 0) horizontal = true;
    if (t.direction.dy !== 0) vertical = true;
    if (horizontal && vertical) break;
  }
  return { horizontal, vertical };
}

/**
 * True when a left/right fold off `arm` would produce a new direction whose
 * axis is already populated by placed tiles. Prevents the chain from
 * spiraling alongside an existing row and stranding an empty arm.
 */
export function foldCreatesParallelRow(
  arm: ArmEndpoint,
  turn: Exclude<Turn, 'auto'>,
  usedAxes: PlacedAxes,
): boolean {
  if (turn === 'straight') return false;
  const newDir = applyTurn(arm.direction, turn);
  if (newDir === null) return false;
  if (newDir.dx !== 0) return usedAxes.horizontal;
  return usedAxes.vertical;
}

type Mode = 'idle' | 'pan' | 'drag' | 'pinch';

interface DragInfo {
  pointerId: number;
  tileIndex: number;
  tile: Tile;
  startScreen: Point;
  currentScreen: Point;
  active: boolean;
}

interface PanInfo {
  pointerId: number;
  lastScreen: Point;
}

interface PinchInfo {
  id1: number;
  id2: number;
  startDist: number;
  startZoom: number;
  /** World point that sat under the midpoint when pinch began — kept under the fingers. */
  startMidWorld: Point;
}

/**
 * Owns the per-frame pointer state machine for the game scene:
 *
 * - **Idle** — no pointers tracked.
 * - **Drag** — primary pointer started on a rack tile and moved past the
 *   drag-start threshold. Tile follows the pointer in screen space; on
 *   release the dispatcher commits if the screen point projects within
 *   `DROP_TOLERANCE_WORLD` of a legal arm endpoint.
 * - **Pan** — primary pointer started on the empty board. Move events
 *   pan the viewport; release returns to idle.
 * - **Pinch** — two simultaneous pointers. Zoom factor = currentDistance /
 *   startDistance; the world point originally under the midpoint stays
 *   under the fingers.
 *
 * Overlay buttons (action bar, result modal, curtain, viewport reset) are
 * hit-tested by `main.ts` BEFORE forwarding to the dispatcher — the
 * dispatcher only sees pointer events that aren't already consumed by UI.
 */
export class PointerDispatcher {
  private readonly activePointers = new Map<number, Point>();
  private mode: Mode = 'idle';
  private drag: DragInfo | null = null;
  private pan: PanInfo | null = null;
  private pinch: PinchInfo | null = null;

  constructor(private readonly opts: PointerDispatcherOptions) {}

  reset(): void {
    this.activePointers.clear();
    this.mode = 'idle';
    this.drag = null;
    this.pan = null;
    this.pinch = null;
  }

  /**
   * Compute every legal drop slot for `tile`, exactly as `snapshot()` and the
   * drag-commit path do — collision-filtered, parallel-axis-filtered, fold-
   * variant-filtered. Keyboard mode (`KeyboardFocusChain`) calls this on
   * arming so the kbd user sees the same set of options a pointer drag would.
   *
   * Returns an empty array when the chain is empty (the lead-only entry is
   * not a slot — callers handle leads via a separate code path), or when no
   * legal arm accepts `tile`.
   */
  enumerateLegalSlots(tile: Tile): readonly CandidateSlot[] {
    const chain = this.opts.getCurrentChain();
    if (chain.lead === null) return [];
    const playCandidates = enumeratePlays(chain, [tile], this.opts.getForcedLeadTile());
    if (playCandidates.length === 0) return [];
    const isDoubleTile = tile[0] === tile[1];
    const layout = this.opts.getLayout();
    const usedAxes = placedAxes(layout.placedTiles);
    const slots: CandidateSlot[] = [];
    const validArmIndices = new Set(playCandidates.map((c) => c.armIndex));
    for (const arm of layout.armEnds) {
      if (!validArmIndices.has(arm.armIndex)) continue;
      for (const turn of TURN_CHOICES) {
        if (arm.empty && turn !== 'straight') continue;
        if (isDoubleTile && turn !== 'straight') continue;
        if (arm.anchorIsDouble && turn !== 'straight') continue;
        if (turn !== 'straight' && foldCreatesParallelRow(arm, turn, usedAxes)) continue;
        const rect = nextTileSlotForTurn(arm, turn, isDoubleTile);
        if (this.slotCollides(rect, layout.placedTiles)) continue;
        slots.push({ armIndex: arm.armIndex, turn, rect });
      }
    }
    return slots;
  }

  /**
   * True when `tile` can legally lead an empty chain (Телефон's forced-lead
   * constraint included). Keyboard mode reads this to decide whether the
   * lead "slot" entry should be present in the focus chain when armed.
   */
  canLeadWith(tile: Tile): boolean {
    const chain = this.opts.getCurrentChain();
    if (chain.lead !== null) return false;
    return enumeratePlays(chain, [tile], this.opts.getForcedLeadTile()).length > 0;
  }

  snapshot(): DispatcherSnapshot {
    if (this.mode !== 'drag' || this.drag === null || !this.drag.active) {
      return EMPTY_SNAPSHOT;
    }
    const drag = this.drag;
    const chain = this.opts.getCurrentChain();
    const playCandidates = enumeratePlays(chain, [drag.tile], this.opts.getForcedLeadTile());
    const canLead = chain.lead === null && playCandidates.length > 0;
    const isDoubleTile = drag.tile[0] === drag.tile[1];
    const layout = this.opts.getLayout();

    // Build the candidate-slot list: per legal arm, try each of the 3 turn
    // options and keep only those whose rect doesn't collide with an
    // existing placed tile. A fold (left/right) is additionally filtered if
    // its new direction is PARALLEL to any already-placed segment — i.e.,
    // the chain would create a row alongside an existing one, which is the
    // setup for the spiral-collision bug where a later play traps an empty
    // arm.
    const usedAxes = placedAxes(layout.placedTiles);
    const candidateSlots: CandidateSlot[] = [];
    if (!canLead) {
      for (const arm of layout.armEnds) {
        if (!playCandidates.some((c) => c.armIndex === arm.armIndex)) continue;
        for (const turn of TURN_CHOICES) {
          // No fold without an anchor tile on the arm — a fold off "nothing"
          // would float detached from the chain.
          if (arm.empty && turn !== 'straight') continue;
          // Doubles always render perpendicular — no fold variants.
          if (isDoubleTile && turn !== 'straight') continue;
          // Folding off a non-spinner double's long side isn't a legal chain
          // extension — only the spinner accepts long-side plays, and those
          // go on the perpendicular arms (2/3), not as folds of 0/1.
          if (arm.anchorIsDouble && turn !== 'straight') continue;
          if (turn !== 'straight' && foldCreatesParallelRow(arm, turn, usedAxes)) continue;
          const rect = nextTileSlotForTurn(arm, turn, isDoubleTile);
          if (this.slotCollides(rect, layout.placedTiles)) continue;
          candidateSlots.push({ armIndex: arm.armIndex, turn, rect });
        }
      }
    }

    // Find the slot whose center the ghost is closest to (within tolerance).
    const worldPt = this.opts.viewport.screenToWorld(drag.currentScreen);
    let snapSlot: { armIndex: number; turn: Exclude<Turn, 'auto'> } | null = null;
    let snapToLead = false;

    if (canLead) {
      const dx = worldPt.x - layout.chainCenter.x;
      const dy = worldPt.y - layout.chainCenter.y;
      if (Math.hypot(dx, dy) <= DROP_TOLERANCE_WORLD) snapToLead = true;
    } else {
      let bestDistSq = DROP_TOLERANCE_WORLD * DROP_TOLERANCE_WORLD;
      for (const slot of candidateSlots) {
        const c = rectCenter(slot.rect);
        const dx = worldPt.x - c.x;
        const dy = worldPt.y - c.y;
        const d2 = dx * dx + dy * dy;
        if (d2 <= bestDistSq) {
          bestDistSq = d2;
          snapSlot = { armIndex: slot.armIndex, turn: slot.turn };
        }
      }
    }

    return {
      draggingTileIndex: drag.tileIndex,
      draggingTile: drag.tile,
      ghostScreen: drag.currentScreen,
      candidateSlots,
      snapSlot,
      canLead,
      snapToLead,
    };
  }

  private slotCollides(slot: TileRect, placed: readonly PlacedTile[]): boolean {
    for (const p of placed) {
      if (rectsOverlap(slot, p.rect)) return true;
    }
    return false;
  }

  handle(event: PointerInteraction): void {
    const screen: Point = { x: event.canvasX, y: event.canvasY };
    switch (event.kind) {
      case 'down':
        this.handleDown(event.pointerId, screen);
        return;
      case 'move':
        this.handleMove(event.pointerId, screen);
        return;
      case 'up':
        this.handleUp(event.pointerId, screen);
        return;
      case 'leave':
      case 'cancel':
        this.handleCancel(event.pointerId);
        return;
    }
  }

  // -------------- private --------------

  private handleDown(pointerId: number, screen: Point): void {
    this.activePointers.set(pointerId, screen);

    // Two simultaneous pointers → enter pinch (cancel any drag/pan).
    if (this.activePointers.size === 2) {
      this.startPinch();
      return;
    }

    // First pointer down: rack tile → maybe-drag (turn-gated), else pan.
    // Pan and pinch are always allowed on the board so the player can scan
    // it on an opponent's turn or during the Muggins claim window. The rack
    // zone is sacred for tile interaction — a click on a rack tile NEVER
    // falls through to pan, even when drag is gated off (input blocked, or
    // no tile at the hit index). Pan only starts on clicks outside the rack.
    const tileIndex = this.hitRack(screen);
    if (tileIndex !== null) {
      if (!this.opts.isInputBlocked()) {
        const hand = this.opts.getLocalHand();
        const tile = hand[tileIndex];
        if (tile) {
          this.drag = {
            pointerId,
            tileIndex,
            tile,
            startScreen: screen,
            currentScreen: screen,
            active: false,
          };
          this.mode = 'drag';
        }
      }
      return;
    }

    this.pan = { pointerId, lastScreen: screen };
    this.mode = 'pan';
  }

  private handleMove(pointerId: number, screen: Point): void {
    if (!this.activePointers.has(pointerId)) return;
    this.activePointers.set(pointerId, screen);

    if (this.mode === 'pinch' && this.pinch) {
      this.updatePinch();
      return;
    }

    if (this.mode === 'drag' && this.drag && this.drag.pointerId === pointerId) {
      this.drag = { ...this.drag, currentScreen: screen };
      if (!this.drag.active) {
        const dx = screen.x - this.drag.startScreen.x;
        const dy = screen.y - this.drag.startScreen.y;
        if (Math.hypot(dx, dy) >= DRAG_START_THRESHOLD) {
          this.drag = { ...this.drag, active: true };
        }
      }
      return;
    }

    if (this.mode === 'pan' && this.pan && this.pan.pointerId === pointerId) {
      const dx = screen.x - this.pan.lastScreen.x;
      const dy = screen.y - this.pan.lastScreen.y;
      if (dx !== 0 || dy !== 0) {
        this.opts.viewport.panBy(dx, dy);
        this.opts.onUserControlledViewport();
        this.pan = { ...this.pan, lastScreen: screen };
      }
      return;
    }
  }

  private handleUp(pointerId: number, _screen: Point): void {
    if (!this.activePointers.has(pointerId)) return;
    this.activePointers.delete(pointerId);

    if (this.mode === 'pinch' && this.pinch) {
      // Either finger up → exit pinch entirely. Remaining pointer (if
      // any) is treated as inert until it lifts — we don't promote to
      // drag/pan mid-gesture to avoid jumps.
      this.pinch = null;
      if (this.activePointers.size === 0) {
        this.mode = 'idle';
      }
      return;
    }

    if (this.mode === 'drag' && this.drag && this.drag.pointerId === pointerId) {
      this.commitOrCancelDrag();
      return;
    }

    if (this.mode === 'pan' && this.pan && this.pan.pointerId === pointerId) {
      this.pan = null;
      this.mode = 'idle';
      return;
    }
  }

  private handleCancel(pointerId: number): void {
    this.activePointers.delete(pointerId);
    if (this.mode === 'pinch') {
      this.pinch = null;
      this.mode = this.activePointers.size === 0 ? 'idle' : 'pinch';
      return;
    }
    if (this.mode === 'drag' && this.drag?.pointerId === pointerId) {
      this.drag = null;
      this.mode = 'idle';
      return;
    }
    if (this.mode === 'pan' && this.pan?.pointerId === pointerId) {
      this.pan = null;
      this.mode = 'idle';
    }
  }

  private startPinch(): void {
    const ids = Array.from(this.activePointers.keys());
    if (ids.length < 2) return;
    const id1 = ids[0]!;
    const id2 = ids[1]!;
    const p1 = this.activePointers.get(id1)!;
    const p2 = this.activePointers.get(id2)!;
    const mid: Point = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
    const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y) || 1;
    const startMidWorld = this.opts.viewport.screenToWorld(mid);
    this.pinch = {
      id1,
      id2,
      startDist: dist,
      startZoom: this.opts.viewport.getState().zoom,
      startMidWorld,
    };
    this.mode = 'pinch';
    // Cancel any in-progress drag/pan.
    this.drag = null;
    this.pan = null;
  }

  private updatePinch(): void {
    if (!this.pinch) return;
    const p1 = this.activePointers.get(this.pinch.id1);
    const p2 = this.activePointers.get(this.pinch.id2);
    if (!p1 || !p2) return;
    const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y) || 1;
    const factor = dist / this.pinch.startDist;
    const targetZoom = this.pinch.startZoom * factor;
    const mid: Point = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
    // resetTo will clamp the zoom; we then offset pan so the start-mid-world
    // stays under the current midpoint.
    const state = this.opts.viewport.getState();
    this.opts.viewport.resetTo(state.panX, state.panY, targetZoom);
    const clampedZoom = this.opts.viewport.getState().zoom;
    const panX = mid.x - this.pinch.startMidWorld.x * clampedZoom;
    const panY = mid.y - this.pinch.startMidWorld.y * clampedZoom;
    this.opts.viewport.resetTo(panX, panY, clampedZoom);
    this.opts.onUserControlledViewport();
  }

  private commitOrCancelDrag(): void {
    const drag = this.drag;
    this.drag = null;
    this.mode = 'idle';
    if (drag === null) return;

    // Sub-threshold release = treat as a no-op tap (drag-and-drop is the
    // only commit path; tap-pick is intentionally not supported).
    if (!drag.active) return;

    const chain = this.opts.getCurrentChain();
    const playCandidates = enumeratePlays(chain, [drag.tile], this.opts.getForcedLeadTile());
    if (playCandidates.length === 0) return;

    const worldPt = this.opts.viewport.screenToWorld(drag.currentScreen);
    const layout = this.opts.getLayout();

    // Lead drop: snap to the chain center within tolerance.
    if (chain.lead === null) {
      const dx = worldPt.x - layout.chainCenter.x;
      const dy = worldPt.y - layout.chainCenter.y;
      if (Math.hypot(dx, dy) <= DROP_TOLERANCE_WORLD) {
        this.opts.onCommitPlay(drag.tile, -1, 'straight');
      }
      return;
    }

    // Mid-chain drop: pick the closest non-colliding (armIdx, turn) slot.
    // Filters here must match snapshot() exactly — anything dropped on a
    // slot the renderer hid would commit a placement the player never saw
    // highlighted.
    const isDoubleTile = drag.tile[0] === drag.tile[1];
    const validArmIndices = new Set(playCandidates.map((c) => c.armIndex));
    const usedAxes = placedAxes(layout.placedTiles);
    let bestSlot: { armIndex: number; turn: Exclude<Turn, 'auto'> } | null = null;
    let bestDistSq = DROP_TOLERANCE_WORLD * DROP_TOLERANCE_WORLD;
    for (const arm of layout.armEnds) {
      if (!validArmIndices.has(arm.armIndex)) continue;
      for (const turn of TURN_CHOICES) {
        if (arm.empty && turn !== 'straight') continue;
        // Doubles always render perpendicular — no fold variants.
        if (isDoubleTile && turn !== 'straight') continue;
        // Folding off a non-spinner double's long side isn't a legal chain
        // extension — see snapshot() for the rule.
        if (arm.anchorIsDouble && turn !== 'straight') continue;
        if (turn !== 'straight' && foldCreatesParallelRow(arm, turn, usedAxes)) continue;
        const rect = nextTileSlotForTurn(arm, turn, isDoubleTile);
        if (this.slotCollides(rect, layout.placedTiles)) continue;
        const c = rectCenter(rect);
        const dx = worldPt.x - c.x;
        const dy = worldPt.y - c.y;
        const d2 = dx * dx + dy * dy;
        if (d2 <= bestDistSq) {
          bestDistSq = d2;
          bestSlot = { armIndex: arm.armIndex, turn };
        }
      }
    }
    if (bestSlot !== null) {
      this.opts.onCommitPlay(drag.tile, bestSlot.armIndex, bestSlot.turn);
    }
  }

  private hitRack(screen: Point): number | null {
    const placements = this.opts.getLayout().rackPlacements;
    for (const p of placements) {
      const { x, y, width, height } = rectBounds(p.rect);
      if (
        screen.x >= x &&
        screen.x <= x + width &&
        screen.y >= y &&
        screen.y <= y + height
      ) {
        return p.index;
      }
    }
    return null;
  }
}

function rectCenter(rect: TileRect): Point {
  const { x, y, width, height } = rectBounds(rect);
  return { x: x + width / 2, y: y + height / 2 };
}
